import React, { useState, useEffect } from 'react';
import { Bug, Loader2, ExternalLink } from 'lucide-react';
import { Button } from '@/components/ui/Button';

/**
 * 🐞 BugReport View
 * Integrates an embedded Google Form for structured feedback collection.
 */
export const BugReport: React.FC = () => {
  const [isLoading, setIsLoading] = useState(true);

  // Safety: If the iframe takes more than 5 seconds, clear the loader 
  // so the user isn't stuck behind a spinner if Google is slow.
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoading(false);
    }, 5000);
    return () => clearTimeout(timer);
  }, []);

  const googleFormUrl = "https://docs.google.com/forms/d/e/1FAIpQLScQiCsiS76DMinLK-_DNogjpnhbHX8qGljFk8EHMfR02bzXjw/viewform?embedded=true";

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-in fade-in slide-in-from-bottom-4 duration-500 pb-10 p-4">
      
      {/* Header Section */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-8">
        <div className="flex items-center gap-4">
          <div className="p-4 bg-rose-50 dark:bg-rose-900/20 rounded-2xl text-rose-600 dark:text-rose-400 shadow-sm border border-rose-100 dark:border-rose-800">
            <Bug className="w-8 h-8" />
          </div>
          <div>
            <h1 className="text-3xl font-black text-slate-900 dark:text-white tracking-tight">
              Report a Bug
            </h1>
            <p className="text-slate-500 dark:text-slate-400 font-medium italic">
              Found a glitch in the matrix? Let the protocols know.
            </p>
          </div>
        </div>
        
        {/* External Link Toggle */}
        <Button 
          variant="ghost" 
          onClick={() => window.open(googleFormUrl.replace('?embedded=true', ''), '_blank')}
          className="text-[10px] font-black uppercase tracking-widest text-slate-400 hover:text-indigo-600 transition-colors"
        >
          <ExternalLink className="w-3 h-3 mr-2" /> Open in New Tab
        </Button>
      </div>

      {/* Form Container */}
      <div className="relative bg-white dark:bg-slate-50 rounded-[2.5rem] border-2 border-slate-100 dark:border-slate-800 shadow-xl overflow-hidden min-h-[850px] transition-all">
        
        {/* Loading Spinner Overlays */}
        {isLoading && (
          <div className="absolute inset-0 flex flex-col items-center justify-center bg-slate-50 dark:bg-slate-900 z-10">
            <Loader2 className="w-10 h-10 text-indigo-500 animate-spin mb-4" />
            <p className="text-xs font-black uppercase tracking-widest text-slate-400">
              Connecting to Feedback System...
            </p>
          </div>
        )}

        {/* Google Forms embed 
            We use filters in dark mode to prevent a blinding white flash.
        */}
        <iframe
          src={googleFormUrl}
          width="100%"
          height="850"
          frameBorder="0"
          marginHeight={0}
          marginWidth={0}
          onLoad={() => setIsLoading(false)}
          className="w-full h-[850px] dark:grayscale-[0.1] dark:invert-[0.02] dark:contrast-[0.95]"
          title="Bug Report Form"
        >
          Loading Feedback Portal…
        </iframe>
      </div>

      {/* Footer Branding */}
      <div className="flex justify-center items-center gap-6 opacity-30 py-8">
        <div className="h-px flex-1 bg-slate-300 dark:bg-slate-700" />
        <p className="text-[10px] font-black uppercase text-slate-400 whitespace-nowrap tracking-[0.3em]">
          EverydayOS System Integrity
        </p>
        <div className="h-px flex-1 bg-slate-300 dark:bg-slate-700" />
      </div>
    </div>
  );
};